<template>
	<div class="resource-booking-modal-footer">
		<div
			v-if="bookingId && !isSplitMode"
			class="resource-booking-modal-footer__actions"
		>
			<SuiTextButton
				v-tooltip="$t('resource_planning_split_booking')"
				:aria-label="$t('resource_planning_split_booking')"
				:aria-pressed="isSplitMode"
				class="resource-booking-modal-footer__icon-split"
				@click="$emit('toggle-split')"
			>
				<SuiIcon name="icon-scissors-16" />
			</SuiTextButton>
			<VDropdown
				:shown="isDeletePopoverOpen"
				:triggers="[]"
				@auto-hide="isDeletePopoverOpen = false"
			>
				<SuiTextButton
					:aria-label="$t('resource-booking__more-actions')"
					class="resource-booking-modal-footer__icon-actions"
					@click="isDeletePopoverOpen = !isDeletePopoverOpen"
				>
					<SuiIcon name="icon-meatballs-16" />
				</SuiTextButton>
				<template #popper>
					<SuiListItem
						icon="icon-bin-16"
						:title="$t('resource-booking__delete')"
						:aria-label="$t('resource-booking__delete')"
						:disabled="isLoadingDelete"
						@click="onDeleteItemClick"
					/>
				</template>
			</VDropdown>
		</div>
		<div class="resource-booking-modal-footer__button-wrapper">
			<SuiButton variant="secondary-outlined" @click="$emit('cancel')">
				{{ $t('cancel') }}
			</SuiButton>
			<SuiButton
				v-tooltip="saveButtonDisabledTooltip"
				variant="primary"
				:is-loading="isLoadingSaving"
				:disabled="isSaveButtonDisabled"
				@click="$emit('save')"
			>
				{{ $t('save') }}
			</SuiButton>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
	SuiButton,
	SuiIcon,
	SuiTextButton,
	SuiListItem,
} from '@developers/scoro-ui';

export interface ResourcePlanningBookingModalFooterProps {
	bookingId?: string;
	isSplitMode: boolean;
	isLoadingDelete: boolean;
	isLoadingSaving: boolean;
	isSaveButtonDisabled: boolean;
	saveButtonDisabledTooltip?: string;
}

const {
	bookingId = undefined,
	isLoadingDelete,
	isLoadingSaving,
	isSaveButtonDisabled,
	saveButtonDisabledTooltip = undefined,
} = defineProps<ResourcePlanningBookingModalFooterProps>();

const emit = defineEmits<{
	(e: 'toggle-split'): void;
	(e: 'delete'): void;
	(e: 'cancel'): void;
	(e: 'save'): void;
}>();

const isDeletePopoverOpen = ref(false);

const onDeleteItemClick = () => {
	isDeletePopoverOpen.value = false;
	emit('delete');
};
</script>

<style lang="scss" scoped>
.resource-booking-modal-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;

	&__actions {
		display: flex;
		align-items: center;
	}

	&__icon-split {
		padding: 1rem;
		--sui-color-link: #{$neutral-60};
	}

	&__icon-actions {
		padding: 1rem;
		border-radius: 0.5rem;
		--sui-color-link: #{$neutral-60};

		&:hover:enabled {
			background-color: $neutral-20;
		}
	}

	&__button-wrapper {
		display: flex;
		gap: 1rem;
		margin-left: auto;
	}
}
</style>
